import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Rocket, CheckCircle2, Circle, ArrowRight, ChevronDown, ChevronUp, Lock,
  Settings, UsersRound, Wallet, Dumbbell, Trophy,
} from 'lucide-react';
import { Card, CardContent, Badge } from './ui';
import { usePermissions } from '../hooks/usePermissions';
import { useOrganization } from '../contexts/OrganizationContext';
import { useEntitlements } from '../contexts/EntitlementsContext';
import {
  supabase,
  getCurrentUsage,
  getCategorias,
  getTemporadas,
  getConceptos,
  getPlanes,
} from '../lib/supabase';

const STORAGE_PREFIX = 'myklubb_onboarding_collapsed_';

// Guia de arranque del club, en "Hoy". Se marca sola con datos reales y NO
// desaparece al completarse: se colapsa a una barrita y se puede reabrir.
//
// Tres reglas que la vuelven util en vez de una lista que regaña:
//
//   1. AGRUPADA POR ETAPA. Antes eran 10 renglones identicos y habia que
//      recorrerlos buscando el primer circulo vacio. Ahora cada bloque cuenta
//      una parte de la historia: configura -> suma gente -> cobra -> opera.
//
//   2. RESPETA EL PLAN. Un club Starter no tiene modulo de Pagos: mostrarle
//      "crea tu plan de pago" como pendiente lo manda a una pantalla de upsell.
//      Los bloques cuyo modulo no incluye su plan se muestran BLOQUEADOS, no
//      pendientes, y no cuentan en el avance -- asi un Starter puede llegar
//      legitimamente al 100% con lo que si tiene.
//
//   3. TORNEOS ES OPCIONAL. Medido en PROD: 0 de 6 clubes reales usan torneos.
//      Si contara, nadie completaria nunca la guia y la tarjeta jamas volveria a
//      colapsarse sola. Se muestra como invitacion, no como deuda.

const ETAPAS = [
  {
    key: 'configura',
    titulo: 'Configura tu club',
    icono: Settings,
    pasos: ['organizacion', 'categorias', 'temporada', 'grupos'],
  },
  {
    key: 'gente',
    titulo: 'Suma a tu gente',
    icono: UsersRound,
    pasos: ['staff', 'jugadores', 'inscripciones', 'jugadores-grupos'],
  },
  {
    key: 'cobra',
    titulo: 'Empieza a cobrar',
    icono: Wallet,
    modulo: 'payments_module',
    resumen: 'Conceptos, planes de pago y estado de cuenta.',
    pasos: ['conceptos', 'plan', 'asignar'],
  },
  {
    key: 'opera',
    titulo: 'Opera tu club',
    icono: Dumbbell,
    pasos: ['horarios', 'asistencia', 'cobro'],
  },
  {
    key: 'compite',
    titulo: 'Compite',
    icono: Trophy,
    opcional: true,
    resumen: 'Cuando tu club entre a un torneo:',
    pasos: ['torneo', 'inscribir-grupo', 'marcador'],
  },
];

const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();
  const { org } = useOrganization();
  const { hasModule, planName, loading: entLoading } = useEntitlements();
  const orgId = org?.id;

  const [pasos, setPasos] = React.useState(null);
  const [collapsed, setCollapsed] = React.useState(null); // null = aún no decidido

  // Preferencia guardada de colapsado (por org). Se respeta aunque la guia crezca
  // con pasos nuevos: quien la tenia cerrada no la ve abrirse sola.
  React.useEffect(() => {
    if (!orgId) return;
    try {
      const v = window.localStorage.getItem(STORAGE_PREFIX + orgId);
      if (v === '1') setCollapsed(true);
      else if (v === '0') setCollapsed(false);
    } catch {
      // localStorage no disponible: se usa el default (colapsada si todo listo)
    }
  }, [orgId]);

  React.useEffect(() => {
    if (!isAdmin || !orgId) return;
    let cancelled = false;
    const cuenta = (tabla) => supabase.from(tabla).select('id', { count: 'exact', head: true });
    (async () => {
      const [
        usage, cats, temps, concs, plans,
        enGrupos, enTemporada, horarios, asistencias, cobros,
        torneos, inscTorneo, juegos,
      ] = await Promise.all([
        getCurrentUsage(orgId),
        getCategorias(),
        getTemporadas(true),
        getConceptos(),
        getPlanes({}),
        // RLS ya limita todos estos conteos a la org actual.
        cuenta('jugadores_grupos'),
        cuenta('temporada_inscripciones'),
        cuenta('series_entrenamiento'),
        cuenta('asistencias'),
        cuenta('pagos_transacciones'),
        cuenta('torneos'),
        cuenta('torneos_inscripciones'),
        cuenta('juegos'),
      ]);
      if (cancelled) return;
      const u = usage?.data || {};
      const hay = (r) => (r?.count || 0) > 0;

      setPasos({
        // Los datos legales alimentan el aviso de privacidad: sin ellos no se
        // pueden pedir consentimientos. Por eso van de primeros. El logo NO
        // cuenta: es cosmetico, esto no.
        organizacion: {
          label: 'Configura tu organización',
          done: !!(org?.razon_social && org?.domicilio_fiscal && org?.correo_privacidad),
          path: '/admin/organizacion',
        },
        categorias: { label: 'Crea tus categorías', done: (cats?.data?.length || 0) > 0, path: '/admin/categorias' },
        temporada: { label: 'Abre una temporada', done: (temps?.data?.length || 0) > 0, path: '/admin/temporadas' },
        grupos: { label: 'Crea tus grupos', done: (u.groups || 0) > 0, path: '/admin/groups' },

        staff: { label: 'Invita a tu staff', done: (u.staff || 0) > 1, path: '/admin/staff' },
        jugadores: { label: 'Registra jugadores', done: (u.players || 0) > 0, path: '/admin/jugadores' },
        // Inscribir va ANTES de agrupar: para entrar a un grupo hay que estar
        // inscrito en la temporada de ese deporte (candado suave).
        inscripciones: { label: 'Inscribe jugadores a la temporada', done: hay(enTemporada), path: '/admin/inscripciones' },
        'jugadores-grupos': { label: 'Asigna jugadores a un grupo', done: hay(enGrupos), path: '/admin/groups' },

        conceptos: { label: 'Define conceptos de cobro', done: (concs?.data?.length || 0) > 0, path: '/admin/pagos/conceptos' },
        plan: { label: 'Crea tu plan de pago', done: (plans?.data?.length || 0) > 0, path: '/admin/pagos/planes' },
        asignar: {
          label: 'Asigna el plan (genera los cargos)',
          done: (plans?.data || []).some((p) => (p.pagos_cargos?.[0]?.count || 0) > 0),
          path: '/admin/pagos/asignaciones',
        },

        horarios: { label: 'Programa los entrenamientos', done: hay(horarios), path: '/admin/entrenamientos-regulares' },
        asistencia: { label: 'Toma tu primera asistencia', done: hay(asistencias), path: '/hoy' },
        cobro: { label: 'Registra tu primer pago', done: hay(cobros), path: '/admin/pagos/registro' },

        torneo: { label: 'Crea un torneo o acepta una invitación', done: hay(torneos), path: '/torneos' },
        'inscribir-grupo': { label: 'Inscribe un grupo', done: hay(inscTorneo), path: '/torneos' },
        marcador: { label: 'Captura tu primer marcador', done: hay(juegos), path: '/torneos/juegos' },
      });
    })();
    return () => { cancelled = true; };
  }, [isAdmin, orgId, org?.razon_social, org?.domicilio_fiscal, org?.correo_privacidad]);

  if (!isAdmin || !pasos || entLoading) return null;

  // Una etapa esta bloqueada si su modulo no viene en el plan. No cuenta en el
  // avance: un Starter debe poder llegar al 100% con lo que si tiene.
  const etapas = ETAPAS.map((e) => {
    const bloqueada = !!e.modulo && !hasModule(e.modulo);
    const items = e.pasos.map((k) => ({ key: k, ...pasos[k] })).filter((p) => p.label);
    const hechos = items.filter((p) => p.done).length;
    return { ...e, bloqueada, items, hechos };
  });

  const contables = etapas.filter((e) => !e.opcional && !e.bloqueada);
  const total = contables.reduce((n, e) => n + e.items.length, 0);
  const doneCount = contables.reduce((n, e) => n + e.hechos, 0);
  const allDone = total > 0 && doneCount === total;
  const isCollapsed = collapsed === null ? allDone : collapsed;

  const setCollapsedPersist = (next) => {
    setCollapsed(next);
    try {
      window.localStorage.setItem(STORAGE_PREFIX + orgId, next ? '1' : '0');
    } catch {
      // sin localStorage solo aplica en esta sesión
    }
  };

  if (isCollapsed) {
    return (
      <Card>
        <CardContent className="py-3">
          <button
            type="button"
            onClick={() => setCollapsedPersist(false)}
            className="flex w-full items-center gap-2 text-left"
            aria-label="Ver primeros pasos"
          >
            <Rocket className="h-4 w-4 shrink-0 text-primary" />
            <span className="flex-1 text-sm font-medium">
              {allDone ? 'Primeros pasos · ¡Todo listo!' : `Primeros pasos · ${doneCount} de ${total}`}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="py-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Rocket className="h-5 w-5 shrink-0 text-primary" />
            <div>
              <p className="font-semibold leading-tight">Primeros pasos</p>
              <p className="text-xs text-muted-foreground">Configura tu club para empezar a operar</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCollapsedPersist(true)}
            className="text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Colapsar primeros pasos"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4">
          <p className="mb-1 text-xs text-muted-foreground">
            {allDone ? '¡Todo listo! Tu club está configurado.' : `${doneCount} de ${total} completados`}
          </p>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: total ? `${(doneCount / total) * 100}%` : '0%' }}
            />
          </div>
        </div>

        <div className="space-y-4">
          {etapas.map((etapa) => {
            const Icono = etapa.icono;
            const completa = !etapa.bloqueada && etapa.hechos === etapa.items.length;
            return (
              <div key={etapa.key}>
                <div className="mb-1.5 flex items-center gap-2">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                      etapa.bloqueada
                        ? 'bg-muted text-muted-foreground'
                        : completa
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-foreground'
                    }`}
                  >
                    {etapa.bloqueada ? <Lock className="h-3.5 w-3.5" /> : <Icono className="h-4 w-4" />}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold">{etapa.titulo}</span>
                  {etapa.bloqueada ? (
                    <Badge variant="secondary" className="shrink-0 text-[10px]">No incluido en {planName}</Badge>
                  ) : etapa.opcional ? (
                    <Badge variant="outline" className="shrink-0 text-[10px]">Opcional</Badge>
                  ) : (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {etapa.hechos} de {etapa.items.length}
                    </span>
                  )}
                </div>

                {etapa.bloqueada ? (
                  // No se listan los pasos: mandarian a una pantalla de upsell.
                  // Se dice que se esta perdiendo y como obtenerlo.
                  <div className="ml-9 rounded-md bg-muted/50 p-3">
                    <p className="text-xs text-muted-foreground">{etapa.resumen}</p>
                    <button
                      type="button"
                      onClick={() => navigate('/billing/plans')}
                      className="mt-1 text-xs font-medium text-primary hover:underline"
                    >
                      Ver planes →
                    </button>
                  </div>
                ) : (
                  <ul className="ml-9 space-y-0.5">
                    {etapa.opcional && etapa.resumen && (
                      <li className="pb-1 text-xs text-muted-foreground">{etapa.resumen}</li>
                    )}
                    {etapa.items.map((p) => (
                      <li key={p.key}>
                        <button
                          type="button"
                          onClick={() => navigate(p.path)}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent"
                        >
                          {p.done ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                          ) : (
                            <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                          <span className={p.done ? 'flex-1 text-muted-foreground line-through' : 'flex-1'}>
                            {p.label}
                          </span>
                          {!p.done && <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
